import { http } from './index';
import { WandImage } from './models';

export type PresetCategory = 'ART_DESIGN_PHOTOGRAPHY' | 'FUN_MEMES' | 'FASHION_OUTFITS_BEAUTY' | 'INTERIOR_DESIGN_PRODUCT';

export interface GenerationPreset {
  id: number;
  title: string;
  prompt: string;
  thumbnail: string;
  images: WandImage[];
  foundation_model: 'CHATGPT' | 'FLUX' | 'NANO_BANANA' | string;
  created_at: string;
  updated_at: string;
  description: string;
  preview_before: string | null;
  preview_after: string | null;
  lora_url: string | null;
  visible: boolean;
  order: number;
  categories: PresetCategory[];
}

export interface CreateGenerationPresetRequest {
  title: string;
  prompt: string;
  description?: string;
  foundation_model: 'CHATGPT' | 'FLUX' | 'NANO_BANANA' | string;
  visible?: boolean;
  lora_url?: string | null;
  uploaded_images?: File[];
  async_processing?: boolean;
  thumbnail?: File | null;
  preview_before?: File | null;
  preview_after?: File | null;
  order?: number;
  categories?: PresetCategory[];
}

export interface UpdateGenerationPresetRequest {
  title?: string;
  prompt?: string;
  description?: string;
  foundation_model?: 'CHATGPT' | 'FLUX' | 'NANO_BANANA' | string;
  visible?: boolean;
  lora_url?: string | null;
  uploaded_images?: File[];
  async_processing?: boolean;
  thumbnail?: File | null;
  preview_before?: File | null;
  preview_after?: File | null;
  order?: number;
  categories?: PresetCategory[];
}

export interface PresetCategoriesResponse {
  preset_categories: PresetCategory[];
}

export async function fetchPresetCategories(): Promise<PresetCategory[]> {
  console.log("📡 [Backend Call] Fetching preset categories...");
  
  const response = await http.get<PresetCategoriesResponse>('/preset-categories/');
  
  console.log("📡 [Backend Response] Preset categories:", response);
  
  return response.preset_categories;
}

export async function fetchGenerationPresets(): Promise<GenerationPreset[]> {
  console.log("📡 [Backend Call] Fetching generation presets...");
  
  const response = await http.get<GenerationPreset[]>('/generation-preset/', {
    headers: {
      'Accept': 'application/json; version=5.0.0'
    }
  });
  
  console.log("📡 [Backend Response] Generation presets:", response);
  
  return response;
}

export async function fetchGenerationPreset(id: number): Promise<GenerationPreset> {
  console.log(`📡 [Backend Call] Fetching generation preset ${id}...`);
  
  const response = await http.get<GenerationPreset>(`/generation-preset/${id}/`);
  
  console.log("📡 [Backend Response] Generation preset:", response);
  
  return response;
}

export async function createGenerationPreset(data: CreateGenerationPresetRequest): Promise<GenerationPreset> {
  console.log("📡 [Backend Call] Creating generation preset...", data);
  
  const formData = new FormData();
  
  // Add text fields
  formData.append('title', data.title);
  formData.append('prompt', data.prompt);
  if (data.description) formData.append('description', data.description);
  formData.append('foundation_model', data.foundation_model);
  formData.append('visible', String(data.visible ?? true));
  if (data.lora_url) formData.append('lora_url', data.lora_url);
  if (data.async_processing !== undefined) formData.append('async_processing', String(data.async_processing));
  
  // Add categories
  if (data.categories) {
    data.categories.forEach(category => {
      formData.append('categories', category);
    });
  }
  
  // Add uploaded images
  if (data.uploaded_images) {
    data.uploaded_images.forEach((file, index) => {
      formData.append(`uploaded_images`, file);
    });
  }

  // Add thumbnail
  if (data.thumbnail) {
    formData.append('thumbnail', data.thumbnail);
  }

  // Add preview images
  if (data.preview_before) {
    formData.append('preview_before', data.preview_before);
  }
  if (data.preview_after) {
    formData.append('preview_after', data.preview_after);
  }
  
  const response = await http.post<GenerationPreset>('/generation-preset/', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  
  console.log("📡 [Backend Response] Created generation preset:", response);
  
  return response;
}

export async function updateGenerationPreset(
  id: number, 
  data: UpdateGenerationPresetRequest
): Promise<GenerationPreset> {
  console.log(`📡 [Backend Call] Updating generation preset ${id}...`, data);
  
  const formData = new FormData();
  
  // Add text fields only if they exist
  if (data.title !== undefined) formData.append('title', data.title);
  if (data.prompt !== undefined) formData.append('prompt', data.prompt);
  if (data.description !== undefined) formData.append('description', data.description);
  if (data.foundation_model !== undefined) formData.append('foundation_model', data.foundation_model);
  if (data.visible !== undefined) formData.append('visible', String(data.visible));
  if (data.lora_url !== undefined) formData.append('lora_url', data.lora_url || '');
  if (data.async_processing !== undefined) formData.append('async_processing', String(data.async_processing));
  
  // Add categories
  if (data.categories !== undefined) {
    data.categories.forEach(category => {
      formData.append('categories', category);
    });
  }
  
  // Add uploaded images
  if (data.uploaded_images) {
    data.uploaded_images.forEach((file, index) => {
      formData.append(`uploaded_images`, file);
    });
  }

  // Add thumbnail
  if (data.thumbnail) {
    formData.append('thumbnail', data.thumbnail);
  }

  // Add preview images
  if (data.preview_before) {
    formData.append('preview_before', data.preview_before);
  }
  if (data.preview_after) {
    formData.append('preview_after', data.preview_after);
  }
  
  const response = await http.patch<GenerationPreset>(`/generation-preset/${id}/`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  
  console.log("📡 [Backend Response] Updated generation preset:", response);
  
  return response;
}

export async function deleteGenerationPreset(id: number): Promise<void> {
  console.log(`📡 [Backend Call] Deleting generation preset ${id}...`);
  
  await http.delete(`/generation-preset/${id}/`);
  
  console.log("📡 [Backend Response] Generation preset deleted successfully");
}